/**
 * Personalized column order helpers (localStorage, per browser/user).
 * Visibility stays on whatever store each dashboard already uses.
 */

export function loadColumnOrder(storageKey) {
  if (!storageKey) return null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((k) => typeof k === 'string') : null;
  } catch {
    return null;
  }
}

export function saveColumnOrder(storageKey, order) {
  if (!storageKey) return;
  try {
    localStorage.setItem(storageKey, JSON.stringify(order));
  } catch (e) {
    console.warn('Failed to save column order:', e);
  }
}

/** Keep saved keys that still exist; append any new keys at the end. */
export function normalizeColumnOrder(savedOrder, allKeys) {
  const known = new Set(allKeys);
  const seen = new Set();
  const next = [];
  for (const key of savedOrder || []) {
    if (known.has(key) && !seen.has(key)) {
      next.push(key);
      seen.add(key);
    }
  }
  for (const key of allKeys) {
    if (!seen.has(key)) next.push(key);
  }
  return next;
}

export function moveColumnInOrder(order, fromIndex, toIndex) {
  if (fromIndex === toIndex) return order;
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= order.length || toIndex >= order.length) {
    return order;
  }
  const next = [...order];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

export function columnKeyOf(col) {
  return col.key || col.id || col.accessor;
}

export function applyColumnOrder(columns, order, keyFn = columnKeyOf) {
  if (!columns?.length) return [];
  const byKey = new Map(columns.map((c) => [keyFn(c), c]));
  const keys = normalizeColumnOrder(order, columns.map(keyFn));
  return keys.map((k) => byKey.get(k)).filter(Boolean);
}
